// Role-based permission helpers.
//
// Authorization now comes from a permission matrix (roles × modules × actions)
// carried in the JWT, not from a two-value role. See src/lib/claims.ts for the
// decoding and migration 20260802000028 for where the claims are stamped.
//
// The legacy `isOwner` / `isCashier` / `isStaff` helpers below still work and
// are still correct — `roles.legacy_role` keeps profiles.role in step with the
// new model, and 29 RLS policies continue to read it. They answer a coarser
// question than the matrix does, so prefer `can(claims, module, action)` in
// anything new or being touched. A `finance` user is legacy-`owner` (they must
// read every event's invoices), and asking `isOwner` about them will happily
// say yes to editing stock, which the database will then refuse.

import type { SessionClaims } from '@/lib/claims';
import { can } from '@/lib/claims';
import type { UserRole } from '@/types/domain';

export const isOwner = (role: UserRole | undefined | null): boolean => role === 'owner';
export const isCashier = (role: UserRole | undefined | null): boolean => role === 'cashier';
export const isStaff = (role: UserRole | undefined | null): boolean =>
  role === 'owner' || role === 'cashier';

/**
 * Which permission a route needs. The route gate and the sidebar both read
 * this, so a role that cannot see a module cannot reach it by typing the URL
 * either — and neither list can drift from the other, because there is one
 * list.
 *
 * Order matters: `/customers/:id` must not be matched by a shorter prefix that
 * happens to sort first, so these are checked longest-prefix-first below.
 */
export const ROUTE_PERMISSIONS: ReadonlyArray<
  readonly [path: string, module: string, action: string]
> = [
  // Every role's landing page; what it shows is gated inside (money needs
  // reports.view). events.view is the one permission every built-in role holds.
  ['/dashboard', 'events', 'view'],
  ['/pos', 'pos', 'view'],
  ['/transactions', 'transactions', 'view'],
  ['/piutang', 'receivables', 'view'],
  ['/returns', 'returns', 'view'],
  ['/inventory', 'inventory', 'view'],
  ['/customers', 'customers', 'view'],
  ['/events', 'events', 'view'],
  ['/reports', 'reports', 'view'],
  ['/invoice', 'transactions', 'view'],
  // Everyone who can be gated by an approval rule needs to be able to raise
  // and track one, so this is deliberately the widest gate on the list.
  ['/approvals', 'pos', 'view'],
  ['/audit-logs', 'reports', 'audit_log'],
  ['/users', 'users', 'view'],
  ['/settings', 'settings', 'view'],
] as const;

/** Routes every signed-in user reaches regardless of role. */
const ALWAYS_ALLOWED = ['/unauthorized', '/config-required', '/public-invoice'];

export function canAccess(
  claims: SessionClaims | null | undefined,
  pathname: string,
): boolean {
  if (!claims?.roleId) return false;
  if (ALWAYS_ALLOWED.some((p) => pathname.startsWith(p))) return true;

  const match = [...ROUTE_PERMISSIONS]
    .sort((a, b) => b[0].length - a[0].length)
    .find(([path]) => pathname === path || pathname.startsWith(`${path}/`));

  // An unlisted path is not a public one. Anything routable that nobody
  // thought to map should be unreachable until it is mapped, rather than open
  // to whoever guesses the URL.
  if (!match) return false;
  return can(claims, match[1], match[2]);
}

/** Route prefixes this session may open. Used by the sidebar. */
export function allowedRoutes(claims: SessionClaims | null | undefined): string[] {
  if (!claims?.roleId) return [];
  return ROUTE_PERMISSIONS.filter(([, m, a]) => can(claims, m, a)).map(([p]) => p);
}
